import React, { useState } from 'react';
import {
  Stack, Text, withTranslation, ModalView, Spinner, Header, Button, StyledContainer, Icon, Trans,
} from '@piwikpro/ui-components';
import { TFunction } from 'react-i18next';
import { withInject } from '@piwikpro/platform';
import { BillingService } from '../../BillingService';

export type ChangeProcessingModalVariant = 'subscription_created' | 'payment_processing' | 'payment_failed' | 'change_scheduled' | 'subscription_updated'


const PaymentProcessingBody: React.FunctionComponent<{
  t: TFunction
}> = (
  { t },
) => (
  <Stack
    vertical
    horizontalAlignment="middle"
    verticalAlignment="center"
    spacing="narrow"
  >
    <Stack.Item previousSpacing="extraWide" />
    <Stack.Item>
      <Spinner />
    </Stack.Item>
    <Stack.Item previousSpacing="none">
      <Header type="small">{t('marketing.change-plan-overlay.payment-processing.description-0')}</Header>
    </Stack.Item>
    <Stack.Item>
      <div style={{ textAlign: 'center' }}>
        <Text>{t('marketing.change-plan-overlay.payment-processing.description-1')}</Text>
      </div>
    </Stack.Item>
    <Stack.Item previousSpacing="extraWide" />
    <Stack.Item />
  </Stack>
);


interface InjectedProps {
  billingService: BillingService
}
interface PaymentFailedBodyProps extends InjectedProps {
  t: TFunction
}

const PaymentFailedBody: React.FunctionComponent<PaymentFailedBodyProps> = (
  { t, billingService },
) => {
  const [isLoading, setIsLoading] = useState(false);

  const openPaddleCustomerPortal = async () => {
    try {
      setIsLoading(true);
      const response = await billingService.getSubscriptionManagementUrl();
      window.open(response.update_payment_method);
      // eslint-disable-next-line no-empty
    } catch {}
    setIsLoading(false);
  };

  return (
    <Stack
      vertical
      horizontalAlignment="middle"
      verticalAlignment="center"
      spacing="narrow"
    >
      <Stack.Item previousSpacing="none">
        <Icon name="exclamation-mark-circle" size="large" color="red" />
      </Stack.Item>
      <Stack.Item>
        <Header type="medium">{t('marketing.change-plan-overlay.payment-failed.description-0')}</Header>
      </Stack.Item>
      <Stack.Item>
        <div style={{ textAlign: 'center' }}>
          <Text>
            <Trans
              i18nKey="marketing.change-plan-overlay.payment-failed.description-1"
              components={[<b>text</b>]}
            />
          </Text>
        </div>
      </Stack.Item>
      <Stack.Item>
        <div style={{ textAlign: 'center' }}>
          <Text>{t('marketing.change-plan-overlay.payment-failed.description-2')}</Text>
        </div>
      </Stack.Item>
      <Stack.Item previousSpacing="default">
        <Button pending={isLoading} icon="external-link" text={t('marketing.change-plan-overlay.payment-failed.button-text')} onClick={() => openPaddleCustomerPortal()} />
      </Stack.Item>
    </Stack>
  );
};

const WrappedPaymentFailedBody = withInject<InjectedProps>({
  billingService: 'MarketingCrate.billingService',
})(withTranslation(['administration'])(PaymentFailedBody));

const SubscriptionUpdatedBody: React.FunctionComponent<{
  onClose: () => void
  t: TFunction
}> = ({
  onClose,
  t,
}) => (
  <Stack
    vertical
    horizontalAlignment="middle"
    verticalAlignment="center"
    spacing="narrow"
  >
    <Stack.Item previousSpacing="extraWide" />
    <Stack.Item>
      <Icon name="checkmark-circle-outline" size="large" color="green" />
    </Stack.Item>
    <Stack.Item>
      <Header type="medium">{t('marketing.change-plan-overlay.subscription-updated.description-0')}</Header>
    </Stack.Item>
    <Stack.Item>
      <div style={{ textAlign: 'center' }}>
        <Text>{t('marketing.change-plan-overlay.subscription-updated.description-1')}</Text>
      </div>
    </Stack.Item>
    <Stack.Item previousSpacing="default">
      <Button text={t('marketing.change-plan-overlay.subscription-updated.button-text')} onClick={() => onClose()} />
    </Stack.Item>
  </Stack>
);

const ChangeScheduledBody: React.FunctionComponent<{
  onClose: () => void
  t: TFunction
}> = ({
  onClose,
  t,
}) => (
  <Stack
    vertical
    horizontalAlignment="middle"
    verticalAlignment="center"
    spacing="narrow"
  >
    <Stack.Item previousSpacing="extraWide" />
    <Stack.Item>
      <Icon name="calendar-check" size="large" color="green" />
    </Stack.Item>
    <Stack.Item>
      <Header type="medium">{t('marketing.change-plan-overlay.change-scheduled.description-0')}</Header>
    </Stack.Item>
    <Stack.Item>
      <div style={{ textAlign: 'center' }}>
        <Text>{t('marketing.change-plan-overlay.change-scheduled.description-1')}</Text>
      </div>
    </Stack.Item>
    <Stack.Item previousSpacing="default">
      <Button text={t('marketing.change-plan-overlay.subscription-updated.button-text')} onClick={() => onClose()} />
    </Stack.Item>
  </Stack>
);


const SubscriptionCreatedBody: React.FunctionComponent<{
  onClose: () => void
  t: TFunction
}> = ({
  onClose,
  t,
}) => (
  <Stack
    vertical
    horizontalAlignment="middle"
    verticalAlignment="center"
    spacing="narrow"
    fullHeight
  >
    <Stack.Item previousSpacing="extraWide" />
    <Stack.Item>
      <Icon name="checkmark-circle-outline" size="extraLarge" color="green" />
    </Stack.Item>
    <Stack.Item previousSpacing="wide">
      <Header type="medium">{t('marketing.change-plan-overlay.subscription-created.description-0')}</Header>
    </Stack.Item>
    <Stack.Item>
      <div style={{ textAlign: 'center' }}>
        <Text>{t('marketing.change-plan-overlay.subscription-created.description-1')}</Text>
      </div>
    </Stack.Item>
    <Stack.Item previousSpacing="default">
      <Button text={t('marketing.change-plan-overlay.subscription-updated.button-text')} onClick={() => onClose()} />
    </Stack.Item>
  </Stack>
);

const getModalBody = (
  {
    variant, onClose, t,
  } :{
    variant: ChangeProcessingModalVariant
    onClose: () => void
    t: TFunction
  },
) => {
  switch (variant) {
    case 'payment_processing':
      return <PaymentProcessingBody t={t} />;

    case 'payment_failed':
      return <WrappedPaymentFailedBody />;

    case 'subscription_updated':
      return <SubscriptionUpdatedBody t={t} onClose={onClose} />;

    case 'change_scheduled':
      return <ChangeScheduledBody t={t} onClose={onClose} />;

    case 'subscription_created':
      return <SubscriptionCreatedBody t={t} onClose={onClose} />;

    default:
      return null;
  }
};


interface ChangeProcessingInfoModalProps {
  variant: ChangeProcessingModalVariant
  onClose: () => void
  t: TFunction
}

const ChangeProcessingInfoModal: React.FunctionComponent<ChangeProcessingInfoModalProps> = (
  {
    t,
    variant,
    onClose,
  },
) => {
  const modalBody = getModalBody({ variant, onClose, t });
  if (!modalBody) return null;

  return (
    <ModalView
      dataId={variant === 'subscription_created' ? 'apply-new-subscription-modal' : 'update-subscription-modal'}
      title=""
    >
      {
        variant === 'payment_failed' && (
          <Stack>
            <Stack.Item fill />
            <Stack.Item>
              <div
                role="button"
                tabIndex={0}
                onClick={() => onClose()}
                onKeyDown={() => {}}
                style={{
                  cursor: 'pointer', display: 'inline-flex', margin: '12px 12px 0px 0px', padding: '4px',
                }}
              >
                <Icon size="medium" color="gray" name="cross" />
              </div>
            </Stack.Item>
          </Stack>
        )
      }
      <StyledContainer fullHeight withoutTopPadding padding="extraWide">
        <StyledContainer fullHeight padding="extraNarrow">
          {modalBody}
        </StyledContainer>
      </StyledContainer>
    </ModalView>
  );
};

export default withTranslation(['administration'])(ChangeProcessingInfoModal);
